import { useCallback } from 'react';
import toast from 'react-hot-toast';
import { useAppSelector } from '../store';
import { selectTaskData, selectCROutput } from '../store/selectors/createCRSelectors';
import { constructionReviewsService } from '../services/construction-reviews.service';
import { CR_AUTO_SAVE } from '../constants/app.constants';

interface AutoSaveOptions {
  /** Overrides the default success toast message (e.g. "BOM saved"). */
  successMessage?: string;
}

interface UseCrAutoSaveResult {
  /** True when a task is selected and the CR can be persisted. */
  canSave: boolean;
  /** Fire-and-forget upsert of the CR record (full form_data snapshot from Redux). */
  autoSave: (options?: AutoSaveOptions) => void;
}

/**
 * Shared fire-and-forget CR auto-save.
 *
 * Persists the entire CR (generated/updated output + structured_data built
 * from the current Redux form_data by the service) via the upsert-by-task
 * create endpoint. Safe to call from any workflow step once a task is selected.
 *
 * @returns {UseCrAutoSaveResult} `canSave` flag and the `autoSave` trigger
 */
export const useCrAutoSave = (): UseCrAutoSaveResult => {
  const taskData = useAppSelector(selectTaskData);
  const crOutput = useAppSelector(selectCROutput);

  const canSave = !!taskData.selected_task;

  const autoSave = useCallback((options?: AutoSaveOptions): void => {
    const selectedTask = taskData.selected_task;
    if (!selectedTask) return;

    const crPayload = {
      task_id: selectedTask.gid,
      task_name: selectedTask.name,
      generated_cr: crOutput?.generated_cr || {},
      updated_cr: crOutput?.updated_cr || {},
      changes: crOutput?.changes || {},
    };

    constructionReviewsService
      .create(crPayload)
      .then(() => {
        toast.success(options?.successMessage ?? CR_AUTO_SAVE.SUCCESS_MESSAGE, {
          duration: CR_AUTO_SAVE.SUCCESS_TOAST_DURATION_MS,
        });
      })
      .catch(() => {
        toast.error(CR_AUTO_SAVE.ERROR_MESSAGE, {
          duration: CR_AUTO_SAVE.ERROR_TOAST_DURATION_MS,
        });
      });
  }, [taskData.selected_task, crOutput]);

  return { canSave, autoSave };
};
